import {
    useState,
    useCallback,
    useEffect,
    useContext
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

// Context
import { AuthContext } from '@providers/Auth';
import { loginRequest } from '@providers/azureADAuthConfig';

// Components
import Loader from '@components/Loader';

// Utils
import { getLoginProvider, getToken } from '@helpers/utils';
import { LoginProviders } from '@helpers/enums';

// APIs
import { signIn } from '@api/requests';

// Styles
import './LoginCallback.scss';

const LoginCallback = () => {
    let navigate = useNavigate();

    const { setIsLoggedIn } = useContext(AuthContext);

    const isLoginAzureADEnabled = (process.env.REACT_APP_LOGIN_AZURE_AD_ENABLED && process.env.REACT_APP_LOGIN_AZURE_AD_ENABLED.toLowerCase() === "true") || false;
    const { instance, accounts } = useMsal();

    const [acquiringToken, setAcquiringToken] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleSubmitLogin = useCallback(async (accessToken?: string) => {
        setErrorMessage('');

        const signInDataValues = {
            userName: '',
            password: '',
            token: accessToken || '',
            provider: LoginProviders.AzureAD,
            remember: false,
        };

        const [err] = await signIn(signInDataValues);
        if (err) {
            let msg = '';

            switch (err) {
                case "Request failed with status code 400":
                    msg = 'Please make sure to contact support. Kindly note this page will be redirected to logout.';
                    break;

                default:
                    msg = 'Something went wrong. Kindly note this page will be redirected to logout.';
                    break;
            }
            setErrorMessage(msg);

            setTimeout(() => {
                instance.logout({
                    account: accounts[0],
                    postLogoutRedirectUri: "https://" + window.location.host + process.env.REACT_APP_LOGOUT_AZURE_AD_REDIRECT_URI
                });
            }, 5000);
        } else {
            setIsLoggedIn(true);
            navigate('/dashboard');
        }
    }, [navigate, setIsLoggedIn, accounts, instance]);

    useEffect(() => {
        const token = getToken();
        if (token) {
            setIsLoggedIn(true);
            navigate('/dashboard');
        }

        const loginProvider = getLoginProvider();
        if (isLoginAzureADEnabled && loginProvider === LoginProviders.AzureAD) {
            // Check if it's redirected from microsoft otherwise redirect it back to /
            if (document.referrer !== "" && document.referrer !== window.location.host) {
                if (!token && !acquiringToken && accounts && accounts[0]) {
                    setAcquiringToken(true);

                    // Silently acquires an access token which is then attached to a request for MS Graph data
                    instance
                        .acquireTokenSilent({
                            ...loginRequest,
                            account: accounts[0],
                        })
                        .then((response) => {
                            // Send with submit login token coming from MSAL
                            handleSubmitLogin(response.accessToken);
                        })
                        .catch((error) => {
                            // Handle timezone exception from MSAL package by reloading the page which will update the timezone
                            if (error instanceof InteractionRequiredAuthError) {
                                console.log("Exception InteractionRequiredAuthError handled successfully");
                                window.location.reload();
                            }
                        });
                }
            }
            else {
                navigate('/');
            }
        }
        else {
            navigate("/");
        }
    }, [isLoginAzureADEnabled, instance, accounts, handleSubmitLogin, acquiringToken, navigate, setIsLoggedIn]);

    return (
        <div className="login-callback__loader">
            <Loader active={true} message={errorMessage} />
        </div>
    );
};

export default LoginCallback;